/**
 * OrganizatorKrokiPage.jsx — Organizatör portalı › Kroki (backlog #322, 2026-10-11; plan:
 * dev/firma-portal/initial_plan.txt "Organizatör Kroki").
 * Route: /Firma/Kroki   side: firma, modül KROKI (organizatör: READ)
 *
 * Organizatör yalnızca KENDİ fuarının AKTİF salonlarını görür (salon_to_fuar; backend
 * GET /portal/organizator/{fuarId}/salonlar, fuar geneli görünüm gerekir). Salon ekleme / çıkarma /
 * düzenleme yok: bunlar SAKA personelinin Salonlar sayfasında. Bir salon seçilince Alan Planı
 * fdsvector'de YALNIZCA GÖRÜNTÜLEME olarak açılır (fdsKroki-embedded):
 *   /embed/stand-plan?fairId=&hallId=&mode=view&viewer=organizer&layers=none&fit=hall&lang=<tr|en>
 * viewer=organizer (fdsVector #323) düzenlemeyi kapatır ve organizatör panelini açar: arama ve vurgu
 * süzgeci, isteğe bağlı stand etiketleri (stand no, katılımcı, kW, alan, tip), tıklanan standın
 * bilgileri ve YALNIZCA PNG görüntü indirme. Çizim aksiyonları sonraki bir sürümde (plan).
 *
 * Çerçevenin geliştirici araçları (Kaynak id paneli, yeni sekmede aç) gizli: organizatör başka bir
 * fuarın id'sini deneyemesin ve oturumsuz bir sekme açılmasın.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Row, Spinner, Table,
} from "reactstrap";
import { Map as MapIcon, Eye } from "lucide-react";
import { usePortal } from "../../Components/Contexts/PortalContext";
import { getOrganizatorSalonlar } from "../../services/portalService";
import KrokiVectorEmbed from "../../Components/Kroki/KrokiVectorEmbed";
import { resolveStandPlanDocument } from "../../services/krokiVectorService";
import { CizimDurumuPill, CIZIM_DURUM } from "../../Components/FairAreas/salonDurumlari";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

/** fdsVector #323'ün organizatör görünümü: salon çerçevesine sığdırılmış, katman paneli yok. */
// WAS: const ORGANIZATOR_GORUNUMU = { viewer: "organizer", layers: "none", fit: "hall", lang: "tr" };
// #339: lang yok — buildEmbedUrl arayüz dilini gönderir; yabancı bir organizatör krokiyi İngilizce görür.
const ORGANIZATOR_GORUNUMU = { viewer: "organizer", layers: "none", fit: "hall" };

const hataMetni = (e) => e?.response?.data?.message || (typeof e?.response?.data === "string" ? e.response.data : null)
  || e?.message || i18n.t("portal:organizatorKroki.salonlarYuklenemedi");

export default function OrganizatorKrokiPage() {
  const { t } = useTranslation("portal");
  const portal = usePortal();
  const organizator = portal?.arayuzler?.find((a) => a.rol === "ORGANIZATOR") ?? null;
  const fuarId = portal?.fuarId;
  const gorunum = Boolean(organizator?.fuarGorunumu);

  const [salonlar, setSalonlar] = useState(null);
  const [hata, setHata] = useState(null);
  const [arama, setArama] = useState("");
  const [secili, setSecili] = useState(null);       // { salonId, salonName }
  const [plan, setPlan] = useState(null);           // { salonId, durum: 'kontrol' | 'yok' | 'ac' }

  useEffect(() => { document.title = i18n.t("portal:organizatorKroki.krokiFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await getOrganizatorSalonlar(fuarId);
        if (!ignore) { setSalonlar(Array.isArray(list) ? list : []); setHata(null); }
      } catch (e) {
        if (!ignore) { setHata(hataMetni(e)); setSalonlar([]); }
      }
    }
    if (fuarId && gorunum) run();
    return () => { ignore = true; };
  }, [fuarId, gorunum]);

  const gorunenler = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr-TR");
    return (salonlar || []).filter((s) => !q || String(s.salonName ?? "").toLocaleLowerCase("tr-TR").includes(q));
  }, [salonlar, arama]);

  /**
   * Bir salonu aç. Önce fdsvector'e "bu salonun planı var mı" diye sorulur (çapraz kaynak iframe'in
   * 404 ekranı okunamıyor): 404 → plan yok, iframe açılmaz. İlk açılışta fdsvector çerezi henüz yoksa
   * soru 401 alır; o zaman iframe yine açılır ve oturumu kendisi aktarır.
   */
  async function salonAc(s) {
    setSecili({ salonId: s.salonId, salonName: s.salonName });
    setPlan({ salonId: s.salonId, durum: "kontrol" });
    const sonuc = await resolveStandPlanDocument({ fairId: fuarId, hallId: s.salonId });
    setPlan((p) => (p?.salonId === s.salonId
      ? { salonId: s.salonId, durum: !sonuc.ok && sonuc.reason === "not-mapped" ? "yok" : "ac" }
      : p));
  }

  if (!organizator) {
    return (
      <div className="page-content"><Container fluid>
        <Alert color="warning" className="mt-3">{t("organizatorKroki.firmanizSeciliFuardaOrganizatorDegil")}</Alert>
      </Container></div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <MapIcon size={18} className="text-muted" />
            <h5 className="card-title mb-0 flex-grow-1">{t("organizatorKroki.kroki")}</h5>
            <Badge color="primary">{organizator.ad}</Badge>
            <Badge color="light" className="text-muted border">{t("organizatorKroki.yalnizcaGoruntuleme")}</Badge>
          </CardHeader>
          <CardBody>
            {!gorunum ? (
              <Alert color="info" className="small mb-0">
                {t("organizatorKroki.fuarGeneliGorunumFirmanizIcin")}
              </Alert>
            ) : (
              <Row className="g-3">
                <Col xl={3} lg={4}>
                  <div className="small text-muted mb-2">
                    {t("organizatorKroki.buFuarinAktifSalonlariSalon")}
                  </div>
                  {(salonlar?.length ?? 0) > 6 && (
                    <Input bsSize="sm" className="mb-2" placeholder={t("organizatorKroki.salonAra")} value={arama}
                      onChange={(e) => setArama(e.target.value)} />
                  )}
                  {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
                  {salonlar == null ? (
                    <div className="text-center py-4"><Spinner size="sm" /></div>
                  ) : salonlar.length === 0 ? (
                    !hata && <div className="text-muted small">{t("organizatorKroki.buFuardaAktifSalonYok")}</div>
                  ) : (
                    <div className="table-responsive">
                      <Table size="sm" hover className="align-middle mb-0">
                        <thead className="table-light small">
                          <tr><th>{t("organizatorKroki.salon")}</th><th className="text-end">{t("organizatorKroki.stand")}</th><th>{t("organizatorKroki.alanPlani")}</th><th /></tr>
                        </thead>
                        <tbody>
                          {gorunenler.map((s) => (
                            <tr key={s.salonId} className={secili?.salonId === s.salonId ? "table-primary" : ""}>
                              <td className="fw-semibold">{s.salonName || `#${s.salonId}`}</td>
                              <td className="text-end small">{Number(s.standSayisi ?? 0).toLocaleString(currentLocaleTag())}</td>
                              <td>
                                <CizimDurumuPill id={`org-alan-${s.salonId}`} plan="alanPlani"
                                  durum={s.alanPlaniDurum ?? CIZIM_DURUM.CIZIM_YOK} />
                              </td>
                              <td className="text-end">
                                <Button size="sm" color="primary" outline title={t("organizatorKroki.krokiyiGoruntule")} onClick={() => salonAc(s)}>
                                  <Eye size={13} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Col>
                <Col xl={9} lg={8}>
                  {!secili ? (
                    <div className="border rounded-3 text-muted text-center py-5 small">
                      {t("organizatorKroki.krokisiniGoruntulemekIcinSoldanBir")}
                    </div>
                  ) : plan?.durum === "kontrol" ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : plan?.durum === "yok" ? (
                    <Alert color="info" className="small">
                      <b>{secili.salonName}</b>{" "}{t("organizatorKroki.salonununBuFuarIcinHenuz")}
                    </Alert>
                  ) : (
                    <>
                      <div className="fw-semibold mb-2">{secili.salonName}{" "}{t("organizatorKroki.alanPlani2")}</div>
                      <KrokiVectorEmbed
                        fairId={fuarId}
                        hallId={secili.salonId}
                        allowEdit={false}
                        gelistiriciAraclari={false}
                        viewOptions={ORGANIZATOR_GORUNUMU}
                        height={680}
                      />
                    </>
                  )}
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
